import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import { Scale, Save, RotateCcw, ExternalLink, Loader2, AlertTriangle } from "lucide-react";

const LegalPagesAdmin = () => {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/legal-pages");
      setPages(data || []);
      setActive((prev) => prev || data?.[0]?.slug || "");
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const page = pages.find((p) => p.slug === active);
  const setBody = (body) => setPages((prev) => prev.map((p) => (p.slug === active ? { ...p, body } : p)));
  const setTitle = (title) => setPages((prev) => prev.map((p) => (p.slug === active ? { ...p, title } : p)));

  const save = async () => {
    if (!page) return;
    setSaving(true);
    try {
      await api.put(`/admin/legal-pages/${page.slug}`, { title: page.title, body: page.body });
      toast.success("Page enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!page || !window.confirm("Restaurer le texte par défaut de cette page ? Vos modifications seront perdues.")) return;
    try {
      const { data } = await api.post(`/admin/legal-pages/${page.slug}/reset`);
      setBody(data.body);
      toast.success("Texte par défaut restauré");
    } catch { toast.error("Erreur"); }
  };

  const incomplete = (page?.body || "").includes("[À compléter]");

  return (
    <div data-testid="legal-pages-admin">
      <div className="mb-5">
        <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800 flex items-center gap-2"><Scale size={24} /> Pages légales</h1>
        <p className="text-stone-500 mt-1 text-sm sm:text-base">Mentions légales, politique de confidentialité, CGU et gestion des cookies. Ces pages sont publiées sur le site et liées dans le pied de page.</p>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {pages.map((p) => (
          <button key={p.slug} onClick={() => setActive(p.slug)} data-testid={`legal-tab-${p.slug}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${active === p.slug ? "bg-deep-rose text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}>
            {p.title}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-400"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : page ? (
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 space-y-4">
          {incomplete && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-sm text-amber-800" data-testid="legal-incomplete-warning">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>Cette page contient des mentions <strong>[À compléter]</strong> : remplacez-les par les informations officielles de l'association (nom déclaré, adresse du siège, RNA/SIRET, responsable de publication, email de contact RGPD).</span>
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Titre de la page</label>
            <input value={page.title} onChange={(e) => setTitle(e.target.value)} data-testid="legal-title-input" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Contenu</label>
            <RichTextEditor value={page.body} onChange={setBody} testId="legal-editor" hideVariables showHeadings />
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full text-sm disabled:opacity-50" data-testid="legal-save-btn">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Enregistrer
            </button>
            <button onClick={reset} className="inline-flex items-center gap-2 bg-stone-100 text-stone-700 px-4 py-2.5 rounded-full text-sm" data-testid="legal-reset-btn">
              <RotateCcw size={15} /> Texte par défaut
            </button>
            <a href={`/${page.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-deep-rose text-sm" data-testid="legal-view-link">
              <ExternalLink size={14} /> Voir la page publique
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LegalPagesAdmin;
